import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  Bell,
  BookOpen,
  CalendarClock,
  CheckCircle2,
  Database,
  FileSearch,
  Globe,
  Heart,
  ListChecks,
  Mail,
  Moon,
  RefreshCw,
  Sun,
  User,
  Webhook,
  XCircle
} from 'lucide-react';
import SingleSelectDropdown from './components/SingleSelectDropdown';
import CollapsiblePanel from './components/CollapsiblePanel';
import EmailAlertsPanel from './components/EmailAlertsPanel';
import WebhookAlertsModal from './components/WebhookAlertsModal';
import ProfileMenu from './components/ProfileMenu';
import PortalNotificationsPanel from './components/PortalNotificationsPanel';
import ResultsPanel from './components/ResultsPanel';
import DocumentComparatorPanel from './components/DocumentComparatorPanel';
import AccountPage from './components/modals/AccountModal';
import AdminModal from './components/modals/AdminModal';
import DocumentLinkModal from './components/modals/DocumentLinkModal';
import ServiceStatusModal from './components/modals/ServiceStatusModal';
import SlackWebhookGuidePage from './components/SlackWebhookGuidePage';
import SupportModal from './components/modals/SupportModal';
import {
  CACHE_TTL_NOTIFICATIONS,

  DEFAULT_API_TOKEN,
  PORTALS,
  getCache,
  setCache
} from './constants/portals';
import { SUPPORT_CONFIG } from './constants/support';
import {
  buildThirdPartyPortalNotice,
  detectSvrsOutageContent,
  getSefazDocumentStatus,
  isSvrsPortal,
  readJsonErrorResponse
} from './utils/portalHelpers';
import { appendApiTokenToUrl, getApiDocsUrl, getApiUrl, isCrossOriginApiRequest } from './utils/api';
import { DOCUMENT_LINK_PATH, DOCUMENT_LINK_QUERY_PARAM, SHORT_LINK_PATH, buildDocumentSharePath } from './utils/documentLinks';
import { extractSeriesKey, getDocumentComparisonKind, resolveDocumentDate } from './utils/documentInsights';
import { showToast } from './utils/toast';

const MOBILE_BREAKPOINT = 1024;
const RESTRICTED_ACCESS_PATH = '/backoffice';
const ACCOUNT_PAGE_PATH = '/conta-api';
const SLACK_WEBHOOK_GUIDE_PATH = '/configurar-slack-webhook';
const TECNOSPEED_MONITOR_DOCS = ['nfe', 'nfce', 'cte'];
const TECNOSPEED_MONITOR_REFERENCE_UF = 'mg';
const BOT_CHECK_INTERVALS_SECONDS = {
  documents: Number(process.env.REACT_APP_BOT_DOCUMENTS_INTERVAL_SECONDS) || 3600,
  serviceStatus: Number(process.env.REACT_APP_BOT_SERVICE_STATUS_INTERVAL_SECONDS) || 300,
  tecnospeed: Number(process.env.REACT_APP_BOT_TECNOSPEED_INTERVAL_SECONDS) || 120,
  heartbeat: Number(process.env.REACT_APP_BOT_HEARTBEAT_INTERVAL_SECONDS) || 60
};
const INITIAL_REQUEST_DEDUPE_MS = 5000;

let lastInitialHeartbeatFetchAt = 0;
let lastInitialServiceStatusRefreshAt = 0;

function getServiceStatusCategory(maintenanceInfo) {
  if (!maintenanceInfo || maintenanceInfo.status === 'operational') return 'operational';
  if (maintenanceInfo.status === 'outage' || maintenanceInfo.kind === 'outage' || maintenanceInfo.kind === 'timeout') return 'stopped';
  if (maintenanceInfo.kind === 'scheduled') return 'planned';
  return 'attention';
}

export default function App() {
  const [selectedPortal, setSelectedPortal] = useState(() => localStorage.getItem('extrator_last_portal') || 'mdfe');
  const [resultsSource, setResultsSource] = useState(null);
  const [isComparatorOpen, setIsComparatorOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);
  const [results, setResults] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [, setMaintenanceInfo] = useState(null);
  const [portalNotifications, setPortalNotifications] = useState([]);
  const [isLoadingPortalNotifications, setIsLoadingPortalNotifications] = useState(false);
  const [portalNotificationsError, setPortalNotificationsError] = useState('');
  const [hasLoadedPortalNotifications, setHasLoadedPortalNotifications] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : false;
  });
  const [isPortalExpanded, setIsPortalExpanded] = useState(true);
  const [isMaintenanceExpanded, setIsMaintenanceExpanded] = useState(false);
  const [hasTouchedMaintenancePanel, setHasTouchedMaintenancePanel] = useState(false);
  const [isPortalNotificationsExpanded, setIsPortalNotificationsExpanded] = useState(true);
  const [isResultsMaximized, setIsResultsMaximized] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [isNotificationMenuOpen, setIsNotificationMenuOpen] = useState(false);
  const [isStatusNotificationMenuOpen, setIsStatusNotificationMenuOpen] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showServiceStatusModal, setShowServiceStatusModal] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [apiToken, setApiToken] = useState(() => DEFAULT_API_TOKEN);
  const [adminToken, setAdminToken] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientNotes, setClientNotes] = useState('');
  const [clients, setClients] = useState([]);
  const [issuedToken, setIssuedToken] = useState('');
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const [isUpdatingClient, setIsUpdatingClient] = useState(null);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [botHeartbeat, setBotHeartbeat] = useState(null);
  const [botClockTick, setBotClockTick] = useState(() => Date.now());
  const [lastExecutionAt, setLastExecutionAt] = useState(null);
  const [serviceStatusItems, setServiceStatusItems] = useState([]);
  const [isLoadingServiceStatusItems, setIsLoadingServiceStatusItems] = useState(false);
  const [webManiaStatusItems, setWebManiaStatusItems] = useState([]);
  const [isLoadingWebManiaStatus, setIsLoadingWebManiaStatus] = useState(false);
  const [webManiaError, setWebManiaError] = useState(false);
  const [tecnoSpeedStatusItems, setTecnoSpeedStatusItems] = useState([]);
  const [isLoadingTecnoSpeedStatus, setIsLoadingTecnoSpeedStatus] = useState(false);
  const [tecnoSpeedError, setTecnoSpeedError] = useState(false);
  const [documentComparisons, setDocumentComparisons] = useState([]);
  const [isLoadingDocumentComparisons, setIsLoadingDocumentComparisons] = useState(false);
  const [documentComparisonsError, setDocumentComparisonsError] = useState('');
  const [selectedComparisonSeriesKey, setSelectedComparisonSeriesKey] = useState('');
  const [selectedComparisonRange, setSelectedComparisonRange] = useState({
    newerDocumentId: '',
    olderDocumentId: ''
  });
  const [documentComparisonAiAction, setDocumentComparisonAiAction] = useState('idle');
  const [selectedSourceDocument, setSelectedSourceDocument] = useState(null); // Documento de origem que abriu a comparação
  const [documentLinkLocation, setDocumentLinkLocation] = useState(() => ({
    path: window.location.pathname,
    search: window.location.search
  }));
  const comparisonRequestIdRef = useRef(0);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  useEffect(() => {
    const syncDocumentLinkLocation = () => {
      setDocumentLinkLocation({
        path: window.location.pathname,
        search: window.location.search
      });
    };

    syncDocumentLinkLocation();
    window.addEventListener('popstate', syncDocumentLinkLocation);
    return () => window.removeEventListener('popstate', syncDocumentLinkLocation);
  }, []);

  useEffect(() => {
    const handleDocumentLinkClick = async (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const anchor = event.target.closest?.('a[href]');
      if (!anchor) return;

      const url = new URL(anchor.href, window.location.origin);
      const isDocumentLink = (
        url.origin === window.location.origin &&
        (
          url.pathname === DOCUMENT_LINK_PATH ||
          url.searchParams.get(DOCUMENT_LINK_QUERY_PARAM) === '1' ||
          url.pathname.startsWith(`${SHORT_LINK_PATH}/`)
        )
      );

      if (!isDocumentLink) return;

      event.preventDefault();
      let nextPath = `${url.pathname}${url.search}`;

      if (url.pathname.startsWith(`${SHORT_LINK_PATH}/`)) {
        const code = url.pathname.slice(SHORT_LINK_PATH.length + 1);
        try {
          const response = await fetch(getApiUrl(`document-links?code=${encodeURIComponent(code)}`), {
            headers: {
              Accept: 'application/json',
              ...(DEFAULT_API_TOKEN ? { 'x-app-token': DEFAULT_API_TOKEN } : {})
            }
          });
          if (!response.ok) throw new Error('short_link_not_found');
          const payload = await response.json();
          nextPath = payload.path || nextPath;
        } catch {
          window.location.assign(url.href);
          return;
        }
      }

      window.history.pushState({}, '', nextPath);
      setDocumentLinkLocation({
        path: window.location.pathname,
        search: window.location.search
      });
    };

    document.addEventListener('click', handleDocumentLinkClick);
    return () => document.removeEventListener('click', handleDocumentLinkClick);
  }, []);

  useEffect(() => {
    const handleLocationChange = () => {
      if (window.location.pathname === RESTRICTED_ACCESS_PATH) {
        setShowAdminModal(true);
      } else {
        setShowAdminModal(false);
      }
    };

    handleLocationChange();

    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (window.location.pathname === ACCOUNT_PAGE_PATH) return;

    if (params.has('verifyEmail')) {
      window.location.replace(`${ACCOUNT_PAGE_PATH}?verifyEmail=${encodeURIComponent(params.get('verifyEmail'))}`);
      return;
    }

    if (params.get('cadastro') === 'api') {
      window.location.replace(ACCOUNT_PAGE_PATH);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileViewport(window.innerWidth < MOBILE_BREAKPOINT);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!apiToken.trim() && DEFAULT_API_TOKEN) {
      setApiToken(DEFAULT_API_TOKEN);
    }
  }, [apiToken]);

  const buildRequestHeaders = useCallback((options = {}) => {
    const trimmedAdminToken = adminToken.trim();
    const trimmedApiToken = apiToken.trim();
    const shouldUseAdminToken = Boolean(options.admin && trimmedAdminToken);
    const headers = {
      Accept: options.accept || 'application/json',
      ...(options.contentType ? { 'Content-Type': options.contentType } : {})
    };

    if (options.includeAuth !== false && trimmedApiToken && !shouldUseAdminToken) {
      headers.Authorization = `Bearer ${trimmedApiToken}`;
    }

    if (shouldUseAdminToken) {
      headers['x-admin-token'] = trimmedAdminToken;
    }

    return headers;
  }, [adminToken, apiToken]);

  const buildAuthenticatedGetUrl = useCallback((requestUrl) => {
    if (!isCrossOriginApiRequest(requestUrl)) {
      return requestUrl;
    }

    return appendApiTokenToUrl(requestUrl, apiToken);
  }, [apiToken]);

  const buildGetRequestOptions = useCallback((requestUrl, options = {}) => ({
    headers: buildRequestHeaders(options)
  }), [buildRequestHeaders]);

  const ensureApiToken = useCallback(() => {
    if (!apiToken.trim()) {
      showToast('Informe um token de acesso para usar a API.', { type: 'warning' });
      return false;
    }

    return true;
  }, [apiToken]);

  const normalizePortalStatus = useCallback((payload, portalLabel) => {
    if (!payload) return null;

    if (payload.source === 'third_party') {
      const status = payload.kind === 'outage' || payload.kind === 'timeout' ? 'outage' : 'scheduled';
      return {
        portal: portalLabel,
        status,
        kind: payload.kind || status,
        title: payload.title || (status === 'outage' ? 'Serviço Indisponível' : 'Aviso de Manutenção'),
        warning: payload.warning || '',
        text: payload.text || '',
        source: null,
        availability: null,
        contingency: null
      };
    }

    const status = payload.status || (payload?.maintenance?.active ? 'scheduled' : 'operational');
    const fallbackText = status === 'operational'
      ? payload?.availability?.source?.evidence || 'Nenhuma instabilidade ou manutenção identificada no serviço.'
      : payload?.statusSource?.evidence || payload?.maintenance?.description || '';

    return {
      portal: payload.portal || portalLabel,
      status,
      kind: payload?.maintenance?.kind || status,
      title: payload?.maintenance?.title || (status === 'operational' ? 'Serviço Operacional' : 'Aviso de Serviço'),
      warning: payload?.maintenance?.warning || '',
      text: payload?.maintenance?.description || fallbackText,
      source: payload?.statusSource || payload?.maintenance?.source || null,
      availability: payload?.availability || null,
      contingency: payload?.contingency || null
    };
  }, []);

  const fetchBotHeartbeat = useCallback(async () => {
    try {
      const botHeartbeatUrl = getApiUrl('bot-heartbeat');
      const hasAdminToken = Boolean(adminToken.trim());
      const requestUrl = botHeartbeatUrl;
      const response = await fetch(requestUrl, {
        ...buildGetRequestOptions(requestUrl, { admin: hasAdminToken })
      });

      if (response.ok) {
        const data = await response.json();
        // Seta os dados normais do heartbeat
        setBotHeartbeat(data);
      } else {
        // Resposta não-ok (ex: 500, 401): sai do skeleton com estado de erro
        console.error('Heartbeat retornou erro HTTP:', response.status);
        setBotHeartbeat({ error: true });
      }
    } catch (error) {
      // Falha de rede ou timeout: sai do skeleton com estado de erro
      console.error('Erro ao carregar heartbeat:', error);
      setBotHeartbeat({ error: true });
    }
  }, [adminToken, buildGetRequestOptions]);

  useEffect(() => {
    const now = Date.now();
    if (now - lastInitialHeartbeatFetchAt > INITIAL_REQUEST_DEDUPE_MS) {
      lastInitialHeartbeatFetchAt = now;
      fetchBotHeartbeat();
    }

    const interval = setInterval(fetchBotHeartbeat, 60000);
    return () => clearInterval(interval);
  }, [fetchBotHeartbeat]);

  useEffect(() => {
    const interval = setInterval(() => setBotClockTick(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const authenticateAdminAccess = useCallback(async () => {
    if (!adminToken.trim()) {
      showToast('Informe o token administrativo para liberar o cadastro.', { type: 'warning' });
      return;
    }

    setIsLoadingClients(true);
    try {
      const response = await fetch(getApiUrl('api-clients'), {
        headers: buildRequestHeaders({ admin: true })
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao validar token administrativo.');
      }

      setIsAdminAuthenticated(true);
      setClients(payload?.clients || []);
      showToast('Acesso administrativo liberado.', { type: 'success' });
    } catch (error) {
      setIsAdminAuthenticated(false);
      showToast(`Falha ao validar admin: ${error.message}`, { type: 'error' });
    } finally {
      setIsLoadingClients(false);
    }
  }, [adminToken, buildRequestHeaders]);

  const loadClients = useCallback(async () => {
    if (!adminToken.trim()) {
      showToast('Informe o token administrativo para carregar os cadastros.', { type: 'warning' });
      return;
    }

    setIsLoadingClients(true);
    try {
      const response = await fetch(getApiUrl('api-clients'), {
        headers: buildRequestHeaders({ admin: true })
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao carregar os cadastros.');
      }

      setClients(payload?.clients || []);
    } catch (error) {
      showToast(`Falha ao carregar cadastros: ${error.message}`, { type: 'error' });
    } finally {
      setIsLoadingClients(false);
    }
  }, [adminToken, buildRequestHeaders]);

  const createClient = useCallback(async () => {
    if (!isAdminAuthenticated) {
      showToast('Libere primeiro o cadastro com o token administrativo.', { type: 'warning' });
      return;
    }

    if (!clientName.trim()) {
      showToast('Informe o nome do cliente antes de gerar o token.', { type: 'warning' });
      return;
    }

    setIsCreatingClient(true);
    setIssuedToken('');

    try {
      const response = await fetch(getApiUrl('api-clients'), {
        method: 'POST',
        headers: buildRequestHeaders({ admin: true, contentType: 'application/json' }),
        body: JSON.stringify({
          name: clientName.trim(),
          email: clientEmail.trim(),
          notes: clientNotes.trim()
        })
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao criar o token.');
      }

      setIssuedToken(payload.token || '');
      setClientName('');
      setClientEmail('');
      setClientNotes('');
      setClients((previous) => payload.client ? [payload.client, ...previous] : previous);
      showToast('Token gerado com sucesso.', { type: 'success' });
    } catch (error) {
      showToast(`Falha ao criar token: ${error.message}`, { type: 'error' });
    } finally {
      setIsCreatingClient(false);
    }
  }, [buildRequestHeaders, clientEmail, clientName, clientNotes, isAdminAuthenticated]);

  const toggleClientStatus = useCallback(async (client) => {
    if (!isAdminAuthenticated) {
      showToast('Libere primeiro o cadastro com o token administrativo.', { type: 'warning' });
      return;
    }

    setIsUpdatingClient(client.id);

    try {
      const response = await fetch(getApiUrl('api-clients'), {
        method: 'PATCH',
        headers: buildRequestHeaders({ admin: true, contentType: 'application/json' }),
        body: JSON.stringify({
          id: client.id,
          isActive: !client.isActive
        })
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao atualizar o cadastro.');
      }

      setClients((previous) => previous.map((item) => item.id === client.id ? payload.client : item));
      showToast(`Token ${payload.client?.isActive ? 'ativado' : 'desativado'} com sucesso.`, { type: 'success' });
    } catch (error) {
      showToast(`Falha ao atualizar cadastro: ${error.message}`, { type: 'error' });
    } finally {
      setIsUpdatingClient(null);
    }
  }, [buildRequestHeaders, isAdminAuthenticated]);

  const processHtml = useCallback((content, portalId) => {
    const portal = PORTALS[portalId];
    const extracted = [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const pageText = doc.body?.textContent || content;

    if (isSvrsPortal(portal) && detectSvrsOutageContent(pageText)) {
      setResults([]);
      return { ok: false, reason: 'outage' };
    }

    if (isSvrsPortal(portal)) {
      const sections = doc.querySelectorAll('.artigo__texto');
      sections.forEach((section) => {
        let category = 'Geral';
        const header = section.previousElementSibling;
        if (header && header.classList.contains('artigo__cabecalho')) {
          const title = header.querySelector('.artigo__titulo');
          if (title) category = title.textContent.trim();
        }

        const items = section.querySelectorAll('.conteudo-lista__item');
        items.forEach((item) => {
          const timeElement = item.querySelector('.conteudo-lista__item__datahora');
          const date = timeElement ? timeElement.textContent.trim() : '';
          const link = item.querySelector('a[onclick*="download_arquivo_estatico"]');

          if (!link) return;

          const displayTitle = link.textContent.trim();
          const onclick = link.getAttribute('onclick');
          const match = onclick.match(/download_arquivo_estatico\s*\(\s*'([^']+)'\s*,\s*(\d+)\s*,\s*'([^']+)'\s*\)/);

          if (!match) return;

          const sistema = match[1];
          const tipoCod = match[2];
          const nomeArquivo = match[3];
          const url = `${portal.baseUrl}/${sistema}/DownloadArquivoEstatico/?sistema=${sistema}&tipoArquivo=${tipoCod}&nomeArquivo=${encodeURIComponent(nomeArquivo)}`;

          // Busca a descrição na classe específica ou em tags <p> (comum no SVRS)
          let descriptionElement = item.querySelector('.conteudo-lista__item__texto');
          let description = descriptionElement ? descriptionElement.textContent.trim() : '';

          if (!description) {
            const paragraphs = item.querySelectorAll('p');
            description = Array.from(paragraphs).map(p => p.textContent.trim()).filter(t => t).join('\n');
          }

          extracted.push({ sistema: sistema.toUpperCase(), tipo: category, nome: displayTitle, url, data: date, description });
        });
      });

      if (extracted.length === 0) {
        const regex = /download_arquivo_estatico\s*\(\s*'([^']+)'\s*,\s*(\d+)\s*,\s*'([^']+)'\s*\)/g;
        let match;
        while ((match = regex.exec(content)) !== null) {
          const sistema = match[1];
          const tipo = match[2];
          const nome = match[3];
          const url = `${portal.baseUrl}/${sistema}/DownloadArquivoEstatico/?sistema=${sistema}&tipoArquivo=${tipo}&nomeArquivo=${encodeURIComponent(nome)}`;
          extracted.push({ sistema: sistema.toUpperCase(), tipo: 'Arquivo', nome, url, data: '-', description: '' });
        }
      }
    } else if (portalId === 'cte' || portalId === 'nfe') {
      const links = doc.querySelectorAll('a[href*="exibirArquivo.aspx"]');

      links.forEach((anchor) => {
        const href = anchor.getAttribute('href');
        const match = href.match(/conteudo=([^"&]+)/);
        if (!match) return;

        const conteudoId = match[1].trim();
        const url = `${portal.baseUrl}/exibirArquivo.aspx?conteudo=${encodeURIComponent(conteudoId)}`;
        const documentStatus = getSefazDocumentStatus(anchor);
        const span = anchor.querySelector('.tituloConteudo');
        let displayTitle = span ? span.textContent.trim() : anchor.textContent.trim();

        let date = '-';
        const dateMatch = displayTitle.match(/(\d{2}\/\d{2}\/\d{4})/);
        if (dateMatch) {
          date = dateMatch[1];
          displayTitle = displayTitle.replace(/[-\s]*Publicada em\s*\d{2}\/\d{2}\/\d{4}[-\s]*/i, '').trim();
        }

        if (date === '-') {
          const parentRow = anchor.closest('tr');
          if (parentRow) {
            const dateCell = parentRow.querySelector('td:nth-child(2), span[id*="data"]');
            if (dateCell) date = dateCell.textContent.trim();
          }
        }

        // A descrição no portal NF-e/CT-e geralmente vem após o link (seguido de um <br />)
        let description = anchor.parentElement ? anchor.parentElement.textContent.replace(displayTitle, '').replace(/Publicada em \d{2}\/\d{2}\/\d{4}/, '').trim() : '';

        // Limpa hífens residuais no começo da descrição
        description = description.replace(/^[\s\-–—]+/, '').trim();

        extracted.push({ sistema: portalId.toUpperCase(), tipo: documentStatus, nome: displayTitle, url, data: date, description });
      });
    }

    setResults(extracted);
    return { ok: true, count: extracted.length, data: extracted };
  }, []);

  const loadPortalNotifications = useCallback(async () => {
    setIsLoadingPortalNotifications(true);
    setPortalNotificationsError('');
    setHasLoadedPortalNotifications(true);

    try {
      if (!apiToken.trim()) {
        throw new Error('Configure API_APPLICATION_TOKEN antes de iniciar ou compilar a aplicação.');
      }

      const cacheKey = 'portal_notifications';
      const cached = getCache(cacheKey);
      if (cached) {
        setPortalNotifications(cached);
        setIsLoadingPortalNotifications(false);
        return;
      }

      const fifteenDaysAgo = new Date();
      fifteenDaysAgo.setDate(fifteenDaysAgo.getDate() - 15);
      const fromDate = fifteenDaysAgo.toISOString().split('T')[0];
      const url = `${getApiUrl('fiscal-documents')}?limit=30&fromDate=${fromDate}`;

      const response = await fetch(url, {
        ...buildGetRequestOptions(url)
      });
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao carregar os últimos documentos.');
      }

      const documents = payload?.documents || [];
      setPortalNotifications(documents);
      setCache(cacheKey, documents, CACHE_TTL_NOTIFICATIONS);
    } catch (error) {
      setPortalNotifications([]);
      setPortalNotificationsError(error.message);
    } finally {
      setIsLoadingPortalNotifications(false);
    }
  }, [apiToken, buildGetRequestOptions]);

  const fetchPortalStatus = useCallback(async (portalId) => {
    const portal = PORTALS[portalId];
    if (!portal) return null;

    try {
      const url = `${getApiUrl('fiscal-status')}?portal=${portalId}`;
      const response = await fetch(url, buildGetRequestOptions(url));
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        if (payload?.source === 'third_party') {
          const notice = buildThirdPartyPortalNotice(portal.label, isSvrsPortal(portal), payload);
          return normalizePortalStatus(notice, portal.label);
        }
        return { error: true };
      }

      return normalizePortalStatus(payload, portal.label);
    } catch (error) {
      console.error('Erro ao buscar status do portal:', error);
      return { error: true };
    }
  }, [buildGetRequestOptions, normalizePortalStatus]);

  const fetchFromSite = useCallback(async (portalId = selectedPortal, options = {}) => {
    const portal = PORTALS[portalId];
    if (!portal) return;

    const requestedMode = options.forceOnline ? 'online' : (portal.hasDatabase ? 'database' : 'online');
    setIsFetching(true);
    setResults([]);
    setResultsSource(null);
    setMaintenanceInfo(null);
    setPortalNotificationsError('');

    try {
      const statusPromise = fetchPortalStatus(portalId);

      if (requestedMode === 'database') {
        if (!ensureApiToken()) return;

        const url = `${getApiUrl('fiscal-documents')}?portal=${portalId}&limit=500`;
        const [statusMaint, response] = await Promise.all([
          statusPromise,
          fetch(url, buildGetRequestOptions(url))
        ]);

        setMaintenanceInfo(statusMaint);
        const payload = await readJsonErrorResponse(response);

        if (!response.ok) {
          throw new Error(payload?.error || 'Falha ao carregar documentos do banco de dados.');
        }

        const documents = payload?.documents || [];
        const executedAt = new Date().toISOString();
        setResults(documents);
        setResultsSource('database');
        setLastExecutionAt(executedAt);
        showToast(`Foram encontrados ${documents.length} documentos em nossa base de dados.`, { type: 'success' });
      } else {
        const backendUrl = `${getApiUrl('fiscal-proxy')}?url=`;
        const requestUrl = backendUrl + encodeURIComponent(portal.targetUrl);
        const [statusMaint, response] = await Promise.all([
          statusPromise,
          fetch(requestUrl, buildGetRequestOptions(requestUrl, { accept: 'text/html,application/json' }))
        ]);

        setMaintenanceInfo(statusMaint);

        if (!response.ok) {
          const payload = await readJsonErrorResponse(response);

          if (payload?.source === 'third_party' && !statusMaint) {
            const notice = buildThirdPartyPortalNotice(portal.label, isSvrsPortal(portal), payload);
            setMaintenanceInfo(notice);
            showToast(notice.text, { type: 'warning', duration: 9000 });
            return;
          }

          if (payload?.source === 'third_party') {
            showToast(statusMaint?.text || 'Ocorreu um erro no portal.', { type: 'warning', duration: 9000 });
            return;
          }

          throw new Error(payload?.error || 'Erro na requisição online');
        }

        const html = await response.text();
        const result = processHtml(html, portalId);

        if (!result.ok) {
          if (!statusMaint) {
            const notice = buildThirdPartyPortalNotice(portal.label, true);
            setMaintenanceInfo(notice);
          }
          showToast('O portal encontra-se indisponível no momento.', { type: 'warning', duration: 9000 });
          return;
        }

        const executedAt = new Date().toISOString();
        setLastExecutionAt(executedAt);
        setResultsSource('online');
        showToast(`Extração online concluída. Foram lidos ${result.count} links.`, { type: 'success' });
      }
    } catch (error) {
      console.error(error);
      showToast(error.message || 'Erro ao comunicar com o servidor', { type: 'error' });
    } finally {
      setIsFetching(false);
    }
  }, [buildGetRequestOptions, ensureApiToken, fetchPortalStatus, processHtml, selectedPortal]);

  useEffect(() => {
    const portal = PORTALS[selectedPortal];
    if (!portal) return;

    localStorage.setItem('extrator_last_portal', selectedPortal);
  }, [selectedPortal]);

  useEffect(() => {
    loadPortalNotifications();
  }, [loadPortalNotifications]);

  const processedResults = useMemo(() => {
    const groups = {};
    const sortedRaw = [...results].sort((left, right) => {
      const leftTime = resolveDocumentDate(left)?.getTime() || 0;
      const rightTime = resolveDocumentDate(right)?.getTime() || 0;
      if (leftTime !== rightTime) return rightTime - leftTime;
      return (right.id || 0) - (left.id || 0);
    });

    sortedRaw.forEach((doc) => {
      const seriesKey = extractSeriesKey(doc);
      if (seriesKey) {
        const kind = getDocumentComparisonKind(doc);
        const groupKey = `${kind}::${seriesKey}`;
        if (!groups[groupKey]) groups[groupKey] = [];
        groups[groupKey].push(doc);
      }
    });

    return results.map((doc) => {
      const seriesKey = extractSeriesKey(doc);
      if (!seriesKey) return doc;
      
      const kind = getDocumentComparisonKind(doc);
      const groupKey = `${kind}::${seriesKey}`;
      const group = groups[groupKey];
      
      if (group && group.length > 1) {
        const newestDoc = group[0];
        if (doc.url !== newestDoc.url) {
          return {
            ...doc,
            discontinued: true,
            replacedByUrl: buildDocumentSharePath(newestDoc, { portalName: PORTALS[selectedPortal]?.label || newestDoc.portalName || newestDoc.portal }),
            replacedByName: newestDoc.nome
          };
        } else {
          return {
            ...doc,
            isLatest: true
          };
        }
      }
      return doc;
    });
  }, [results, selectedPortal]);

  const filteredResults = useMemo(() => processedResults.filter((item) =>
    item.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.tipo && item.tipo.toLowerCase().includes(searchTerm.toLowerCase()))
  ), [processedResults, searchTerm]);

  const groupedResults = useMemo(() => filteredResults.reduce((accumulator, item) => {
    const category = item.tipo || 'Geral';
    if (!accumulator[category]) accumulator[category] = [];
    accumulator[category].push(item);
    return accumulator;
  }, {}), [filteredResults]);

  const shouldSuggestOnlineSearch = Boolean(
    PORTALS[selectedPortal]?.hasDatabase && resultsSource === 'database' && lastExecutionAt
  );

  const searchOnlineAfterContingency = useCallback(() => {
    fetchFromSite(selectedPortal, { forceOnline: true });
  }, [fetchFromSite, selectedPortal]);

  const comparableSeriesMap = useMemo(() => {
    const counts = new Map();
    const urlToSeries = {};

    results.forEach((item) => {
      const seriesKey = extractSeriesKey(item);
      if (!seriesKey) return;
      counts.set(seriesKey, (counts.get(seriesKey) || 0) + 1);
      urlToSeries[item.url] = seriesKey;
    });

    return Object.fromEntries(
      Object.entries(urlToSeries).filter(([, seriesKey]) => (counts.get(seriesKey) || 0) >= 2)
    );
  }, [results]);

  const pluralize = useCallback((count, singular, plural = `${singular}s`) => (
    count === 1 ? `${count} ${singular}` : `${count} ${plural}`
  ), []);

  const serviceStatusSummary = useMemo(() => {
    const totals = serviceStatusItems.reduce((accumulator, item) => {
      accumulator[getServiceStatusCategory(item.maintenanceInfo)] += 1;

      return accumulator;
    }, {
      attention: 0,
      operational: 0,
      planned: 0,
      stopped: 0
    });

    return {
      ...totals,
      total: serviceStatusItems.length
    };
  }, [serviceStatusItems]);

  const webManiaStatusSummary = useMemo(() => {
    const totals = webManiaStatusItems.reduce((accumulator, item) => {
      if (item.status === 'OPERATIONAL') {
        accumulator.operational += 1;
      } else if (item.status === 'MAJOROUTAGE') {
        accumulator.stopped += 1;
      } else {
        accumulator.scheduled += 1;
      }

      return accumulator;
    }, {
      operational: 0,
      scheduled: 0,
      stopped: 0
    });

    return {
      ...totals,
      total: webManiaStatusItems.length
    };
  }, [webManiaStatusItems]);

  const tecnoSpeedStatusSummary = useMemo(() => {
    const sourceItems = tecnoSpeedStatusItems.flatMap((item) => (
      Array.isArray(item.states) && item.states.length > 0 ? item.states : [item]
    ));

    const totals = sourceItems.reduce((accumulator, item) => {
      if (item.severity === 'operational' || item.status === 'normal') {
        accumulator.operational += 1;
      } else if (item.severity === 'outage' || item.status === 'timeout' || item.status === 'error') {
        accumulator.stopped += 1;
      } else {
        accumulator.attention += 1;
      }

      return accumulator;
    }, {
      operational: 0,
      attention: 0,
      stopped: 0
    });

    return {
      ...totals,
      total: sourceItems.length
    };
  }, [tecnoSpeedStatusItems]);

  const statusConfig = useMemo(() => {
    if (isLoadingServiceStatusItems && serviceStatusItems.length === 0) {
      return {
        bgClass: 'bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700/50',
        icon: RefreshCw,
        iconBgClass: 'bg-gray-100 dark:bg-gray-700/60 text-gray-500 dark:text-gray-300',
        text: 'Verificando o status global dos serviços disponíveis para consulta.',
        textClass: 'text-gray-600 dark:text-gray-300',
        title: 'Verificando serviços',
        titleClass: 'text-gray-600 dark:text-gray-300'
      };
    }

    if (serviceStatusSummary.stopped > 0) {
      return {
        badgeBgClass: 'bg-red-200 dark:bg-red-800/40 border-red-300 dark:border-red-700/50',
        badgeDotClass: 'bg-red-600 dark:bg-red-400',
        badgeTextClass: 'text-red-900 dark:text-red-100',
        bgClass: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-900/40',
        icon: XCircle,
        iconBgClass: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
        text: `${pluralize(serviceStatusSummary.stopped, 'serviço parado', 'serviços parados')}, ${pluralize(serviceStatusSummary.planned, 'agendado', 'agendados')}, ${pluralize(serviceStatusSummary.attention, 'em atenção', 'em atenção')} e ${pluralize(serviceStatusSummary.operational, 'operacional', 'operacionais')}.`,
        textClass: 'text-red-700/80 dark:text-red-200/60',
        title: 'Serviços com indisponibilidade',
        titleClass: 'text-red-600 dark:text-red-400',
        warning: pluralize(serviceStatusSummary.stopped, 'parado', 'parados')
      };
    }

    if (serviceStatusSummary.attention > 0) {
      return {
        badgeBgClass: 'bg-amber-200 dark:bg-amber-800/40 border-amber-300 dark:border-amber-700/50',
        badgeDotClass: 'bg-amber-600 dark:bg-amber-400',
        badgeTextClass: 'text-amber-900 dark:text-amber-100',
        bgClass: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-900/40',
        icon: AlertCircle,
        iconBgClass: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
        text: `${pluralize(serviceStatusSummary.attention, 'serviço em atenção', 'serviços em atenção')}, ${pluralize(serviceStatusSummary.planned, 'agendado', 'agendados')} e ${pluralize(serviceStatusSummary.operational, 'operacional', 'operacionais')}.`,
        textClass: 'text-amber-700/80 dark:text-amber-200/60',
        title: 'Serviços com atenção',
        titleClass: 'text-amber-600 dark:text-amber-400',
        warning: pluralize(serviceStatusSummary.attention, 'em atenção', 'em atenção')
      };
    }

    if (serviceStatusSummary.planned > 0) {
      return {
        badgeBgClass: 'bg-sky-200 dark:bg-sky-800/40 border-sky-300 dark:border-sky-700/50',
        badgeDotClass: 'bg-sky-600 dark:bg-sky-400',
        badgeTextClass: 'text-sky-900 dark:text-sky-100',
        bgClass: 'bg-sky-50 dark:bg-sky-900/20 border-sky-200 dark:border-sky-900/40',
        icon: CalendarClock,
        iconBgClass: 'bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400',
        text: `${pluralize(serviceStatusSummary.planned, 'serviço com evento agendado', 'serviços com evento agendado')} e ${pluralize(serviceStatusSummary.operational, 'serviço operacional', 'serviços operacionais')}.`,
        textClass: 'text-sky-700/80 dark:text-sky-200/60',
        title: 'Serviços com agendamento',
        titleClass: 'text-sky-600 dark:text-sky-400',
        warning: pluralize(serviceStatusSummary.planned, 'agendado', 'agendados')
      };
    }

    return {
      bgClass: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-900/40',
      icon: CheckCircle2,
      iconBgClass: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
      text: 'Todos os serviços estão operacionais.',
      textClass: 'text-emerald-700/80 dark:text-emerald-200/60',
      title: 'Serviços operacionais',
      titleClass: 'text-emerald-600 dark:text-emerald-400'
    };
  }, [isLoadingServiceStatusItems, pluralize, serviceStatusItems.length, serviceStatusSummary]);

  const isAnyServiceStatusLoading = isLoadingServiceStatusItems || isLoadingWebManiaStatus || isLoadingTecnoSpeedStatus;

  const openProfileModal = useCallback((modalName) => {
    setShowProfileMenu(false);
    if (modalName === 'support') {
      setShowSupportModal(true);
      return;
    }

    if (modalName === 'cadastro') {
      window.location.href = ACCOUNT_PAGE_PATH;
      return;
    }

    if (modalName === 'admin') {
      window.history.pushState({}, '', RESTRICTED_ACCESS_PATH);
      setShowAdminModal(true);
      return;
    }

    if (modalName === 'about') {
      window.location.href = '/sobre';
      return;
    }

    if (modalName === 'api') {
      window.open(getApiDocsUrl(), '_blank', 'noopener,noreferrer');
    }
  }, []);

  const closeAdminModal = useCallback(() => {
    if (window.location.pathname === RESTRICTED_ACCESS_PATH) {
      window.history.pushState({}, '', '/');
    }

    setShowAdminModal(false);
  }, []);

  const handlePortalChange = useCallback((portalId) => {
    setSelectedPortal(portalId);
    setMaintenanceInfo(null);
    setResultsSource(null);
  }, []);

  const toggleCategory = useCallback((category) => {
    setExpandedCategories((previous) => ({ ...previous, [category]: !previous[category] }));
  }, []);

  const formatCompactDateTime = useCallback((value) => {
    if (!value) return '--';

    const date = new Date(value);
    const dayMonth = date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit'
    });
    const hourMinute = date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });

    return `${dayMonth} ${hourMinute}`;
  }, []);

  const formatLongDateTime = useCallback((value) => {
    if (!value) return 'Sem registro';

    return new Date(value).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }, []);

  const formatRemainingTime = useCallback((value, intervalSeconds) => {
    if (!value) return 'sem previsão';

    const nextCheckAt = new Date(value).getTime() + (intervalSeconds * 1000);
    const remainingSeconds = Math.max(0, Math.ceil((nextCheckAt - botClockTick) / 1000));

    if (remainingSeconds <= 0) return 'agora';

    const hours = Math.floor(remainingSeconds / 3600);
    const minutes = Math.floor((remainingSeconds % 3600) / 60);
    const seconds = remainingSeconds % 60;
    const parts = [];

    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0 || hours > 0) parts.push(`${minutes}min`);
    parts.push(`${seconds}s`);

    return parts.join(' ');
  }, [botClockTick]);

  const botHeartbeatChecks = useMemo(() => {
    if (!botHeartbeat || botHeartbeat.error) return [];

    return [
      {
        id: 'documents',
        label: 'Documentos fiscais',
        icon: Database,
        value: botHeartbeat.last_documents_check_at || botHeartbeat.last_worker_execution_at,
        intervalSeconds: BOT_CHECK_INTERVALS_SECONDS.documents
      },
      {
        id: 'service-status',
        label: 'Status dos serviços',
        icon: ListChecks,
        value: botHeartbeat.last_service_status_check_at || botHeartbeat.last_worker_execution_at,
        intervalSeconds: BOT_CHECK_INTERVALS_SECONDS.serviceStatus
      },
      {
        id: 'tecnospeed',
        label: 'TecnoSpeed',
        icon: RefreshCw,
        value: botHeartbeat.last_tecnospeed_status_check_at,
        intervalSeconds: BOT_CHECK_INTERVALS_SECONDS.tecnospeed
      },
      {
        id: 'heartbeat',
        label: 'Processo do BOT',
        icon: Heart,
        value: botHeartbeat.last_heartbeat_at,
        intervalSeconds: BOT_CHECK_INTERVALS_SECONDS.heartbeat
      }
    ];
  }, [botHeartbeat]);

  const loadAllServiceStatuses = useCallback(async () => {
    const statusPortals = Object.values(PORTALS).filter((portal) => portal.hasStatus);

    setIsLoadingServiceStatusItems(true);

    try {
      const items = await Promise.all(statusPortals.map(async (portal) => ({
        id: portal.id,
        label: portal.label,
        maintenanceInfo: await fetchPortalStatus(portal.id)
      })));

      setServiceStatusItems(items);
    } catch (error) {
      showToast('Não foi possível carregar o status consolidado dos serviços.', { type: 'error' });
    } finally {
      setIsLoadingServiceStatusItems(false);
    }
  }, [fetchPortalStatus]);

  const loadWebManiaStatus = useCallback(async () => {
    setIsLoadingWebManiaStatus(true);
    setWebManiaError(false);
    try {
      const backendUrl = `${getApiUrl('fiscal-proxy')}?url=`;
      const targetUrl = 'https://monitorsefaz.webmaniabr.com/v3/components.json';
      const requestUrl = backendUrl + encodeURIComponent(targetUrl);

      const response = await fetch(requestUrl, {
        ...buildGetRequestOptions(requestUrl, { accept: 'application/json' })
      });

      if (!response.ok) {
        throw new Error('Falha ao buscar status da Web Mania');
      }

      const data = await response.json();
      setWebManiaStatusItems(data.components || []);
    } catch (error) {
      console.error('Erro ao carregar status Web Mania:', error);
      setWebManiaStatusItems([]);
      setWebManiaError(true);
    } finally {
      setIsLoadingWebManiaStatus(false);
    }
  }, [buildGetRequestOptions]);

  const loadTecnoSpeedStatus = useCallback(async () => {
    setIsLoadingTecnoSpeedStatus(true);
    setTecnoSpeedError(false);

    try {
      const items = await Promise.all(TECNOSPEED_MONITOR_DOCS.map(async (doc) => {
        const requestUrl = `${getApiUrl('tecnospeed-monitor')}?filter-doc=${doc}&filter-uf=${TECNOSPEED_MONITOR_REFERENCE_UF}`;
        const response = await fetch(requestUrl, {
          ...buildGetRequestOptions(requestUrl, { accept: 'application/json' })
        });

        if (!response.ok) {
          throw new Error(`Falha ao buscar status TecnoSpeed para ${doc}`);
        }

        return response.json();
      }));

      setTecnoSpeedStatusItems(items);
    } catch (error) {
      console.error('Erro ao carregar status TecnoSpeed:', error);
      setTecnoSpeedStatusItems([]);
      setTecnoSpeedError(true);
    } finally {
      setIsLoadingTecnoSpeedStatus(false);
    }
  }, [buildGetRequestOptions]);

  const refreshServiceStatuses = useCallback(async () => {
    await loadAllServiceStatuses();
    loadWebManiaStatus();
    loadTecnoSpeedStatus();
  }, [loadAllServiceStatuses, loadTecnoSpeedStatus, loadWebManiaStatus]);

  const loadDocumentComparisons = useCallback(async (options = {}) => {
    const {
      generateAi = false,
      forceAiRefresh = false,
      forceRefresh = false, // Parâmetro para forçar recarregamento de PDF/TXT
      range = null // Faixa explícita opcional para evitar atrasos na atualização de estado do React
    } = options;
    const requestId = comparisonRequestIdRef.current + 1;
    comparisonRequestIdRef.current = requestId;
    const portal = PORTALS[selectedPortal];
    if (!portal?.hasDatabase) {
      setDocumentComparisons([]);
      setDocumentComparisonsError('Este portal ainda não possui histórico suficiente no banco para comparação.');
      return;
    }

    if (!selectedComparisonSeriesKey) {
      setDocumentComparisons([]);
      setDocumentComparisonsError('Não foi possível identificar a série do documento selecionado para comparação.');
      return;
    }

    if (!ensureApiToken()) {
      return;
    }

    setIsLoadingDocumentComparisons(true);
    setDocumentComparisonsError('');
    // Define a ação visual do loader com base nos parâmetros
    setDocumentComparisonAiAction(
      forceRefresh 
        ? 'refresh_docs' 
        : (generateAi ? (forceAiRefresh ? 'refresh' : 'generate') : 'idle')
    );

    try {
      const params = new URLSearchParams({
        portal: selectedPortal,
        seriesKey: selectedComparisonSeriesKey,
        limit: '500'
      });

      const documentKind = selectedSourceDocument ? getDocumentComparisonKind(selectedSourceDocument) : '';
      if (documentKind) {
        params.set('documentKind', documentKind);
      }

      // Usa os IDs do range explícito se informados, caso contrário lê do estado
      const newerId = range ? range.newerDocumentId : selectedComparisonRange.newerDocumentId;
      const olderId = range ? range.olderDocumentId : selectedComparisonRange.olderDocumentId;

      if (newerId) {
        params.set('newerDocumentId', String(newerId));
      }

      if (olderId) {
        params.set('olderDocumentId', String(olderId));
      }

      if (generateAi) {
        params.set('generateAi', 'true');
      }

      if (forceAiRefresh) {
        params.set('forceAiRefresh', 'true');
      }

      // Adiciona o parâmetro forceRefresh à URL se ativo
      if (forceRefresh) {
        params.set('forceRefresh', 'true');
      }

      const requestUrl = `${getApiUrl('fiscal-document-comparisons')}?${params.toString()}`;
      const response = await fetch(requestUrl, buildGetRequestOptions(requestUrl));
      const payload = await readJsonErrorResponse(response);

      if (!response.ok) {
        throw new Error(payload?.error || 'Falha ao carregar o comparador de documentos.');
      }

      if (comparisonRequestIdRef.current !== requestId) {
        return;
      }

      const comparisons = Array.isArray(payload?.comparisons) ? payload.comparisons : [];
      setDocumentComparisons(comparisons);

      const resolvedRange = comparisons?.[0]?.selectedRange;
      if (resolvedRange?.newerId && resolvedRange?.olderId) {
        setSelectedComparisonRange({
          newerDocumentId: String(resolvedRange.newerId),
          olderDocumentId: String(resolvedRange.olderId)
        });
      }

      if (comparisons.length === 0) {
        setDocumentComparisonsError('A série selecionada ainda não possui histórico suficiente para comparação.');
      }
    } catch (error) {
      if (comparisonRequestIdRef.current !== requestId) {
        return;
      }
      setDocumentComparisons([]);
      setDocumentComparisonsError(error.message);
    } finally {
      if (comparisonRequestIdRef.current !== requestId) {
        return;
      }
      setIsLoadingDocumentComparisons(false);
      setDocumentComparisonAiAction('idle');
    }
  }, [
    buildGetRequestOptions,
    ensureApiToken,
    selectedComparisonRange.newerDocumentId,
    selectedComparisonRange.olderDocumentId,
    selectedComparisonSeriesKey,
    selectedSourceDocument,
    selectedPortal
  ]);

  const openDocumentComparison = useCallback((document) => {
    const seriesKey = extractSeriesKey(document);
    setSelectedSourceDocument(document);
    setSelectedComparisonSeriesKey(seriesKey || '');
    setSelectedComparisonRange({
      newerDocumentId: '',
      olderDocumentId: ''
    });
    setDocumentComparisons([]); // Limpa comparações anteriores para forçar a tela de configuração
    setDocumentComparisonsError('');
    setIsComparatorOpen(true);
  }, []);

  const closeDocumentComparison = useCallback(() => {
    comparisonRequestIdRef.current += 1;
    setIsComparatorOpen(false);
    setSelectedSourceDocument(null);
    setSelectedComparisonSeriesKey('');
    setSelectedComparisonRange({
      newerDocumentId: '',
      olderDocumentId: ''
    });
    setDocumentComparisons([]); // Limpa ao fechar
    setDocumentComparisonsError('');
  }, []);

  const handleComparisonRangeChange = useCallback((range) => {
    const nextRange = {
      newerDocumentId: range?.newerDocumentId || '',
      olderDocumentId: range?.olderDocumentId || ''
    };

    setSelectedComparisonRange((current) => {
      if (
        String(current.newerDocumentId || '') === String(nextRange.newerDocumentId || '') &&
        String(current.olderDocumentId || '') === String(nextRange.olderDocumentId || '')
      ) {
        return current;
      }

      return nextRange;
    });

    if (nextRange.newerDocumentId && nextRange.olderDocumentId) {
      loadDocumentComparisons({
        generateAi: true,
        range: nextRange
      });
    }
  }, [loadDocumentComparisons]);

  // Callback para iniciar manualmente o pipeline completo de comparação com IA
  const handleStartComparison = useCallback((range) => {
    setSelectedComparisonRange({
      newerDocumentId: String(range.newerDocumentId),
      olderDocumentId: String(range.olderDocumentId)
    });
    loadDocumentComparisons({
      generateAi: true, // Habilita a IA por padrão no pipeline principal
      range: {
        newerDocumentId: String(range.newerDocumentId),
        olderDocumentId: String(range.olderDocumentId)
      }
    });
  }, [loadDocumentComparisons]);

  const generateComparisonAi = useCallback(() => {
    loadDocumentComparisons({ generateAi: true, forceAiRefresh: false });
  }, [loadDocumentComparisons]);

  const refreshComparisonAi = useCallback(() => {
    loadDocumentComparisons({ generateAi: true, forceAiRefresh: true });
  }, [loadDocumentComparisons]);

  // Callback para forçar o recarregamento dos arquivos PDF/TXT do portal
  const forceRefreshDocuments = useCallback(() => {
    loadDocumentComparisons({ forceRefresh: true });
  }, [loadDocumentComparisons]);

  const openServiceStatusModal = useCallback(() => {
    setShowServiceStatusModal(true);
    refreshServiceStatuses();
  }, [refreshServiceStatuses]);

  const closeDocumentLinkModal = useCallback(() => {
    window.history.pushState({}, '', '/');
    setDocumentLinkLocation({
      path: window.location.pathname,
      search: window.location.search
    });
  }, []);


  const updateIntervalSeconds = useMemo(() => Number(process.env.REACT_APP_STATUS_UPDATE_INTERVAL) || 180, []);
  const [timeLeftToUpdate, setTimeLeftToUpdate] = useState(updateIntervalSeconds);

  const formatCountdown = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const now = Date.now();
    if (now - lastInitialServiceStatusRefreshAt <= INITIAL_REQUEST_DEDUPE_MS) return;

    lastInitialServiceStatusRefreshAt = now;
    refreshServiceStatuses();
  }, [refreshServiceStatuses]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeftToUpdate((prev) => {
        if (prev <= 1) {
          refreshServiceStatuses();
          return updateIntervalSeconds;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [updateIntervalSeconds, refreshServiceStatuses]);

  useEffect(() => {
    if (isLoadingServiceStatusItems) return;
    if (hasTouchedMaintenancePanel) return;
    setIsMaintenanceExpanded(serviceStatusSummary.stopped > 0 || serviceStatusSummary.attention > 0 || serviceStatusSummary.planned > 0);
  }, [hasTouchedMaintenancePanel, isLoadingServiceStatusItems, serviceStatusSummary.attention, serviceStatusSummary.planned, serviceStatusSummary.stopped]);

  // Filtra as versões disponíveis da mesma série no front-end para exibição na tela de configuração
  const availableVersionsForSeries = useMemo(() => {
    if (!selectedSourceDocument || !results) return [];
    const sourceSeriesKey = selectedComparisonSeriesKey;
    if (!sourceSeriesKey) return [];
    const sourceComparisonKind = getDocumentComparisonKind(selectedSourceDocument);
    
    const filtered = results.filter((doc) => {
      const key = extractSeriesKey(doc);
      return (
        key &&
        key.toLowerCase() === sourceSeriesKey.toLowerCase() &&
        getDocumentComparisonKind(doc) === sourceComparisonKind
      );
    });

    return filtered.map((row) => ({
      id: Number(row.id),
      portalSlug: row.portal_slug,
      portalName: row.portal_name,
      sistema: row.sistema,
      tipo: row.tipo,
      nome: row.nome,
      description: row.description,
      url: row.url,
      data: row.document_date_br || row.data || '',
      documentDate: row.document_date || null,
      createdAt: row.created_at || null
    }));
  }, [selectedSourceDocument, results, selectedComparisonSeriesKey]);

  const StatusIcon = statusConfig.icon;
  const shouldRenderComparatorInsideResults = isMobileViewport;
  const showStandaloneComparator = isComparatorOpen && !shouldRenderComparatorInsideResults;
  const isAccountPage = window.location.pathname === ACCOUNT_PAGE_PATH;
  const isSlackWebhookGuidePage = window.location.pathname === SLACK_WEBHOOK_GUIDE_PATH;
  const documentLinkParams = new URLSearchParams(documentLinkLocation.search);
  const isDocumentLinkPage = (
    documentLinkLocation.path === DOCUMENT_LINK_PATH ||
    documentLinkParams.get(DOCUMENT_LINK_QUERY_PARAM) === '1'
  );
  const comparatorPanel = (
    <DocumentComparatorPanel
      comparisons={documentComparisons}
      sourceDocument={selectedSourceDocument}
      availableVersions={availableVersionsForSeries} // Passa a lista pré-carregada para configuração inicial
      error={documentComparisonsError}
      isFetching={isLoadingDocumentComparisons}
      aiAction={documentComparisonAiAction}
      onBack={closeDocumentComparison}
      onCompareRange={handleComparisonRangeChange}
      onStartComparison={handleStartComparison} // Dispara o pipeline de IA manualmente
      onGenerateAi={generateComparisonAi}
      onRefreshAi={refreshComparisonAi}
      onForceRefreshDocs={forceRefreshDocuments}
      getApiUrl={getApiUrl}
      buildAuthenticatedGetUrl={buildAuthenticatedGetUrl}
      onRetry={loadDocumentComparisons}
    />
  );

  if (isAccountPage) {
    return (
      <AccountPage
        getApiUrl={getApiUrl}
      />
    );
  }

  if (isSlackWebhookGuidePage) {
    return <SlackWebhookGuidePage />;
  }

  if (showAdminModal) {
    return (
      <AdminModal
        apiToken={apiToken}
        buildRequestHeaders={buildRequestHeaders}
        getApiUrl={getApiUrl}
        portals={PORTALS}
        adminToken={adminToken}
        authenticateAdminAccess={authenticateAdminAccess}
        clientEmail={clientEmail}
        clientName={clientName}
        clientNotes={clientNotes}
        clients={clients}
        createClient={createClient}
        isAdminAuthenticated={isAdminAuthenticated}
        isCreatingClient={isCreatingClient}
        isLoadingClients={isLoadingClients}
        isUpdatingClient={isUpdatingClient}
        issuedToken={issuedToken}
        loadClients={loadClients}
        onAdminTokenChange={(value) => {
          setAdminToken(value);
          setIsAdminAuthenticated(false);
        }}
        onClientEmailChange={setClientEmail}
        onClientNameChange={setClientName}
        onClientNotesChange={setClientNotes}
        onClose={closeAdminModal}
        toggleClientStatus={toggleClientStatus}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 p-3 sm:p-4 lg:p-8 font-sans">
      <div className={`mx-auto flex w-full max-w-[112rem] flex-col min-h-[calc(100vh-1.5rem)] sm:min-h-[calc(100vh-2rem)] lg:min-h-0 lg:h-[90vh] ${isComparatorOpen ? 'lg:overflow-hidden' : ''}`}>
        <header className="mb-4 sm:mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-black text-gray-800 dark:text-white uppercase tracking-tight flex items-center gap-2">
              <img
                src="/icon-master.png"
                alt=""
                aria-hidden="true"
                className="h-9 w-9 shrink-0 rounded-xl object-contain"
              />
              Monitor Fiscal
            </h1>
            <p className="text-sm text-gray-500 break-words mt-1">SEFAZ & SVRS - Documentos Técnicos e Monitoramento de Serviços</p>
          </div>

          <div className="flex flex-nowrap items-stretch justify-end gap-2 sm:gap-3">
            {/* Skeleton: exibido apenas enquanto botHeartbeat for null (primeira carga) */}
            {!botHeartbeat && (
              <div className="flex h-10 flex-1 min-w-0 items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-3 rounded-xl shadow-sm animate-pulse md:w-48">
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16 shrink-0 md:mb-1.5"></div>
                <div className="h-2.5 bg-gray-200 dark:bg-gray-700 rounded flex-1"></div>
              </div>
            )}

            {/* Estado de erro: exibido quando a função bot-heartbeat falha */}
            {botHeartbeat?.error && (
              <div className="flex h-10 flex-1 min-w-0 items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-3 rounded-xl shadow-sm md:min-w-[13rem]">
                <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 shrink-0">
                  <span className="relative flex h-2 w-2">
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-gray-400 dark:bg-gray-500"></span>
                  </span>
                  Bot Offline
                </div>
                <span className="text-[8px] text-gray-400 dark:text-gray-500 truncate flex-1 text-right">indisponível</span>
              </div>
            )}

            {/* Estado normal: dados do heartbeat carregados com sucesso */}
            {botHeartbeat && !botHeartbeat.error && (
              <div className="group relative flex h-10 flex-1 min-w-0 items-center justify-between gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-3 rounded-xl shadow-sm md:min-w-[13rem]">
                <div className="flex min-w-0 items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-teal-600 dark:text-teal-400 shrink-0">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
                  </span>
                  Bot Online
                </div>
                <div className="min-w-0 text-right font-medium">
                  <div className="flex items-center justify-end gap-2 text-[8px] text-gray-500 dark:text-gray-400">
                    <span className="inline-flex min-w-0 items-center gap-1">
                      <Database size={10} className="shrink-0" />
                      <span className="truncate">{formatCompactDateTime(botHeartbeat.last_worker_execution_at)}</span>
                    </span>
                    <span className="inline-flex min-w-0 items-center gap-1">
                      <Heart size={10} className="shrink-0" />
                      <span className="truncate">{formatCompactDateTime(botHeartbeat.last_heartbeat_at)}</span>
                    </span>
                  </div>
                </div>
                <div className="pointer-events-none absolute right-0 top-full z-50 mt-2 w-80 rounded-xl border border-gray-200 bg-white p-3 text-left shadow-2xl opacity-0 translate-y-1 transition-all duration-150 group-hover:opacity-100 group-hover:translate-y-0 dark:border-gray-700 dark:bg-gray-900">
                  <div className="absolute -top-1.5 right-6 h-3 w-3 rotate-45 border-l border-t border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900" />
                  <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">Escutas do BOT</p>
                  <div className="mt-2 space-y-2">
                    {botHeartbeatChecks.map((check) => {
                      const Icon = check.icon;

                      return (
                        <div key={check.id} className="flex items-start gap-2 rounded-lg bg-gray-50 px-2 py-1.5 dark:bg-gray-800/70">
                          <Icon size={12} className="mt-0.5 shrink-0 text-teal-600 dark:text-teal-400" />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <p className="truncate text-[10px] font-black uppercase tracking-widest text-gray-700 dark:text-gray-200">{check.label}</p>
                              <p className="shrink-0 text-[9px] font-bold text-teal-600 dark:text-teal-400">
                                Próxima em {formatRemainingTime(check.value, check.intervalSeconds)}
                              </p>
                            </div>
                            <p className="mt-0.5 text-[10px] text-gray-500 dark:text-gray-400">
                              Última: {formatLongDateTime(check.value)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={() => setIsDarkMode((previous) => !previous)}
              className="h-10 w-10 flex items-center justify-center rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all shadow-sm"
              title={isDarkMode ? 'Mudar para Modo Claro' : 'Mudar para Modo Escuro'}
            >
              {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            <button
              onClick={() => window.open(getApiDocsUrl(), '_blank', 'noopener,noreferrer')}
              aria-label="Abrir documentação da API"
              className="h-10 w-10 flex items-center justify-center rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all shadow-sm"
              title="Documentação da API"
            >
              <BookOpen size={16} />
            </button>

            <div className="relative">
              <button
                onClick={() => setShowProfileMenu((previous) => !previous)}
                className="h-10 w-10 flex items-center justify-center rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all shadow-sm"
                title="Perfil"
              >
                <User size={16} />
              </button>

              {showProfileMenu ? (
                <ProfileMenu onSelect={openProfileModal} />
              ) : null}
            </div>
          </div>
        </header>

        <div className={`flex flex-col lg:flex-row gap-4 sm:gap-6 flex-1 min-h-0 overflow-visible lg:overflow-hidden ${isComparatorOpen ? 'pb-0' : 'pb-24'} lg:pb-0`}>
          <div className="lg:w-80 xl:w-84 flex flex-col gap-4 shrink-0 min-h-0 pb-2 lg:pb-0 lg:overflow-hidden">
            <CollapsiblePanel
              collapsedSummary={(
                <div className="text-[10px] leading-snug font-bold text-left text-gray-500 dark:text-gray-400">
                  {PORTALS[selectedPortal]?.label}
                </div>
              )}
              expanded={isPortalExpanded}
              icon={FileSearch}
              iconClassName="bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400"
              onToggle={() => setIsPortalExpanded((previous) => !previous)}
              title="Portal SEFAZ"
            >
              <div className="px-4 pt-1 pb-4 space-y-3">
                <SingleSelectDropdown
                  options={Object.values(PORTALS).map((portal) => ({
                    value: portal.id,
                    label: portal.label,
                    icon: portal.hasDatabase ? Database : Globe
                  }))}
                  selectedOption={selectedPortal}
                  onSelectionChange={handlePortalChange}
                  title="Selecionar Portal"
                  Icon={Globe}
                  heightClass="h-9"
                />

                <button
                  onClick={() => fetchFromSite(selectedPortal)}
                  disabled={isFetching}
                  className={`w-full h-9 flex items-center justify-center gap-2 rounded-xl font-bold text-[10px] uppercase tracking-wider transition-all active:scale-[0.98] disabled:opacity-50 ${PORTALS[selectedPortal]?.hasDatabase
                    ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md shadow-blue-500/20'
                    : 'bg-teal-600 text-white hover:bg-teal-700 shadow-md shadow-teal-500/20'
                    }`}
                >
                  <RefreshCw className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`} />
                  {isFetching ? 'Buscando...' : 'Consultar documentos'}
                </button>
              </div>
            </CollapsiblePanel>
            {Object.values(PORTALS).some((portal) => portal.hasStatus) ? (
              <CollapsiblePanel
                collapsedSummary={(
                  <div className="flex items-center gap-3 h-4 w-full">
                    {isLoadingServiceStatusItems ? (
                      <div className="flex items-center gap-1.5 text-[10px] leading-snug font-bold text-gray-500 dark:text-gray-400">
                        <RefreshCw size={10} className="animate-spin text-gray-400" />
                        <span>Sincronizando serviços...</span>
                      </div>
                    ) : (
                      <>
                        <div className="text-[9px] font-black text-gray-400 dark:text-gray-500 tabular-nums bg-gray-100 dark:bg-gray-800/50 px-1.5 py-0.5 rounded-md border border-gray-200/50 dark:border-gray-700/50 mr-1">
                          {formatCountdown(timeLeftToUpdate)}
                        </div>
                        <div className="flex items-center gap-1.5" title="Operacionais">
                          <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.4)]" />
                          <span className="text-[10px] font-black text-gray-600 dark:text-gray-400">{serviceStatusSummary.operational}</span>
                        </div>
                        <div className="flex items-center gap-1.5" title="Agendados">
                          <div className="h-1.5 w-1.5 rounded-full bg-sky-500 shadow-[0_0_6px_rgba(14,165,233,0.4)]" />
                          <span className="text-[10px] font-black text-gray-600 dark:text-gray-400">{serviceStatusSummary.planned}</span>
                        </div>
                        <div className="flex items-center gap-1.5" title="Em Atenção">
                          <div className="h-1.5 w-1.5 rounded-full bg-amber-500 shadow-[0_0_6px_rgba(245,158,11,0.4)]" />
                          <span className="text-[10px] font-black text-gray-600 dark:text-gray-400">{serviceStatusSummary.attention}</span>
                        </div>
                        <div className="flex items-center gap-1.5" title="Indisponíveis">
                          <div className="h-1.5 w-1.5 rounded-full bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.4)]" />
                          <span className="text-[10px] font-black text-gray-600 dark:text-gray-400">{serviceStatusSummary.stopped}</span>
                        </div>
                      </>
                    )}
                  </div>
                )}
                expanded={isMaintenanceExpanded}
                icon={StatusIcon}
                iconClassName={`${statusConfig.iconBgClass} ${isAnyServiceStatusLoading ? 'animate-pulse' : ''}`}
                onToggle={() => {
                  setHasTouchedMaintenancePanel(true);
                  setIsMaintenanceExpanded((previous) => !previous);
                }}
                title="Status dos Serviços"
                extraHeaderAction={(
                  <div className="relative" onMouseLeave={() => setIsStatusNotificationMenuOpen(false)}>
                    <div className={`absolute right-full top-1/2 -translate-y-1/2 pr-2 flex items-center gap-2 transition-all duration-500 ease-out origin-right ${isStatusNotificationMenuOpen ? 'opacity-100 scale-100 translate-x-0' : 'opacity-0 scale-90 translate-x-4 pointer-events-none'}`}>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowEmailModal(true); setIsStatusNotificationMenuOpen(false); }}
                          title="Notificações por E-mail"
                          className="h-8 w-8 flex items-center justify-center rounded-xl bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border border-emerald-200 dark:border-emerald-500/30 text-emerald-600 dark:text-emerald-400 shadow-xl hover:scale-110 active:scale-95 transition-all"
                        >
                          <Mail size={14} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowWebhookModal(true); setIsStatusNotificationMenuOpen(false); }}
                          title="Configurar Webhook"
                          className="h-8 w-8 flex items-center justify-center rounded-xl bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border border-indigo-200 dark:border-indigo-500/30 text-indigo-600 dark:text-indigo-400 shadow-xl hover:scale-110 active:scale-95 transition-all"
                        >
                          <Webhook size={14} />
                        </button>
                      </div>
                    </div>

                    <button
                      onClick={(e) => { e.stopPropagation(); setIsStatusNotificationMenuOpen(!isStatusNotificationMenuOpen); }}
                      onMouseEnter={() => setIsStatusNotificationMenuOpen(true)}
                      className={`h-8 w-8 flex items-center justify-center rounded-xl transition-all relative z-10 ${isStatusNotificationMenuOpen ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/40 rotate-12' : 'bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-blue-600 dark:hover:text-blue-400 border border-gray-100 dark:border-gray-700/50 shadow-sm'}`}
                    >
                      <Bell size={14} className={isStatusNotificationMenuOpen ? 'animate-bounce' : ''} />
                    </button>
                  </div>
                )}
              >
                <div className="px-4 pb-4">
                  {isLoadingServiceStatusItems ? (
                    <div className="grid grid-cols-3 gap-2 animate-pulse">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="h-16 bg-gray-100 dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700" />
                      ))}
                      <div className="col-span-3 h-10 mt-1 bg-gray-100 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700" />
                    </div>
                  ) : (
                    <>
                      <div className="py-2.5 px-3 bg-gray-50/50 dark:bg-gray-900/40 rounded-2xl border border-gray-200/60 dark:border-gray-800 flex items-center justify-between gap-3">
                        <span className="text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">Sefaz (Oficial)</span>
                        <div className="flex shrink-0 items-center gap-2.5">
                          <div className="flex items-center gap-1" title="Operacionais">
                            <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.35)]" />
                            <span className="text-[11px] font-black text-gray-800 dark:text-gray-100 leading-none">{serviceStatusSummary.operational}</span>
                          </div>
                          <div className="flex items-center gap-1" title="Agendados">
                            <div className="h-1.5 w-1.5 rounded-full bg-sky-500 shadow-[0_0_8px_rgba(14,165,233,0.35)]" />
                            <span className="text-[11px] font-black text-gray-800 dark:text-gray-100 leading-none">{serviceStatusSummary.planned}</span>
                          </div>
                          <div className="flex items-center gap-1" title="Em Atenção">
                            <div className="h-1.5 w-1.5 rounded-full bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.35)]" />
                            <span className="text-[11px] font-black text-gray-800 dark:text-gray-100 leading-none">{serviceStatusSummary.attention}</span>
                          </div>
                          <div className="flex items-center gap-1" title="Parados">
                            <div className="h-1.5 w-1.5 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.35)]" />
                            <span className="text-[11px] font-black text-gray-800 dark:text-gray-100 leading-none">{serviceStatusSummary.stopped}</span>
                          </div>
                        </div>
                      </div>

                      {isLoadingWebManiaStatus && webManiaStatusItems.length === 0 ? (
                        <div className="mt-3 py-2 px-3 bg-gray-50/50 dark:bg-gray-900/40 rounded-xl border border-gray-100 dark:border-gray-800/60 flex items-center justify-between">
                          <span className="text-[8px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">API Web Mania</span>
                          <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 dark:text-gray-400">
                            <RefreshCw size={10} className="animate-spin" />
                            <span>Carregando</span>
                          </div>
                        </div>
                      ) : webManiaStatusItems.length > 0 && (
                        <div className="mt-3 py-2 px-3 bg-gray-50/50 dark:bg-gray-900/40 rounded-xl border border-gray-100 dark:border-gray-800/60 flex items-center justify-between">
                          <span className="text-[8px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">API Web Mania</span>
                          <div className="flex gap-3">
                            <div className="flex items-center gap-1.5" title="Operacionais">
                              <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                              <span className="text-[10px] font-bold text-gray-600 dark:text-gray-300">{webManiaStatusSummary.operational}</span>
                            </div>
                            <div className="flex items-center gap-1.5" title="Em Atenção">
                              <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                              <span className="text-[10px] font-bold text-gray-600 dark:text-gray-300">{webManiaStatusSummary.scheduled}</span>
                            </div>
                            <div className="flex items-center gap-1.5" title="Parados">
                              <div className="h-1.5 w-1.5 rounded-full bg-red-500" />
                              <span className="text-[10px] font-bold text-gray-600 dark:text-gray-300">{webManiaStatusSummary.stopped}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {isLoadingTecnoSpeedStatus && tecnoSpeedStatusItems.length === 0 ? (
                        <div className="mt-3 py-2 px-3 bg-gray-50/50 dark:bg-gray-900/40 rounded-xl border border-gray-100 dark:border-gray-800/60 flex items-center justify-between">
                          <span className="text-[8px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">TecnoSpeed</span>
                          <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 dark:text-gray-400">
                            <RefreshCw size={10} className="animate-spin" />
                            <span>Carregando</span>
                          </div>
                        </div>
                      ) : tecnoSpeedStatusItems.length > 0 && (
                        <div className="mt-3 py-2 px-3 bg-gray-50/50 dark:bg-gray-900/40 rounded-xl border border-gray-100 dark:border-gray-800/60 flex items-center justify-between">
                          <span className="text-[8px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">TecnoSpeed</span>
                          <div className="flex gap-3">
                            <div className="flex items-center gap-1.5" title="Normais">
                              <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                              <span className="text-[10px] font-bold text-gray-600 dark:text-gray-300">{tecnoSpeedStatusSummary.operational}</span>
                            </div>
                            <div className="flex items-center gap-1.5" title="Em Atenção">
                              <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                              <span className="text-[10px] font-bold text-gray-600 dark:text-gray-300">{tecnoSpeedStatusSummary.attention}</span>
                            </div>
                            <div className="flex items-center gap-1.5" title="Parados">
                              <div className="h-1.5 w-1.5 rounded-full bg-red-500" />
                              <span className="text-[10px] font-bold text-gray-600 dark:text-gray-300">{tecnoSpeedStatusSummary.stopped}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      <button
                        onClick={openServiceStatusModal}
                        className="mt-3 w-full h-10 flex items-center justify-center gap-2 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-[10px] font-black uppercase tracking-widest text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all active:scale-95 shadow-sm"
                      >
                        <ListChecks size={14} />
                        Ver Todos os Serviços
                      </button>

                    </>
                  )}
                </div>
              </CollapsiblePanel>
            ) : null}

            <PortalNotificationsPanel
              error={portalNotificationsError}
              expanded={isPortalNotificationsExpanded}
              hasLoaded={hasLoadedPortalNotifications}
              isLoading={isLoadingPortalNotifications}
              items={portalNotifications}
              onToggle={() => setIsPortalNotificationsExpanded(!isPortalNotificationsExpanded)}
              extraHeaderAction={(
                <div className="relative" onMouseLeave={() => setIsNotificationMenuOpen(false)}>
                  <div className={`absolute right-full top-1/2 -translate-y-1/2 pr-2 flex items-center gap-2 transition-all duration-500 ease-out origin-right ${isNotificationMenuOpen ? 'opacity-100 scale-100 translate-x-0' : 'opacity-0 scale-90 translate-x-4 pointer-events-none'}`}>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowEmailModal(true); setIsNotificationMenuOpen(false); }}
                        title="Notificações por E-mail"
                        className="h-8 w-8 flex items-center justify-center rounded-xl bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border border-emerald-200 dark:border-emerald-500/30 text-emerald-600 dark:text-emerald-400 shadow-xl hover:scale-110 active:scale-95 transition-all"
                      >
                        <Mail size={14} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowWebhookModal(true); setIsNotificationMenuOpen(false); }}
                        title="Configurar Webhook"
                        className="h-8 w-8 flex items-center justify-center rounded-xl bg-white/90 dark:bg-gray-800/90 backdrop-blur-md border border-indigo-200 dark:border-indigo-500/30 text-indigo-600 dark:text-indigo-400 shadow-xl hover:scale-110 active:scale-95 transition-all"
                      >
                        <Webhook size={14} />
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={(e) => { e.stopPropagation(); setIsNotificationMenuOpen(!isNotificationMenuOpen); }}
                    onMouseEnter={() => setIsNotificationMenuOpen(true)}
                    className={`h-8 w-8 flex items-center justify-center rounded-xl transition-all relative z-10 ${isNotificationMenuOpen ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/40 rotate-12' : 'bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-blue-600 dark:hover:text-blue-400 border border-gray-100 dark:border-gray-700/50 shadow-sm'}`}
                  >
                    <Bell size={14} className={isNotificationMenuOpen ? 'animate-bounce' : ''} />
                  </button>
                </div>
              )}
            />
          </div>

          <div className="flex-1 min-h-0 flex flex-col gap-4 overflow-hidden">
            <div className="flex-1 min-h-0 overflow-hidden">
              {!showStandaloneComparator ? (
                <ResultsPanel
                  comparableSeriesMap={comparableSeriesMap}
                  comparatorBadgeCount={documentComparisons.length}
                  comparatorContent={comparatorPanel}
                  expandedCategories={expandedCategories}
                  filteredResults={filteredResults}
                  groupedResults={groupedResults}
                  isFetching={isFetching}
                  isComparatorOpen={isComparatorOpen}
                  isMaximized={isResultsMaximized}
                  lastExecutionAt={lastExecutionAt}
                  onCompareDocument={openDocumentComparison}
                  onCloseModal={() => setIsResultsMaximized(false)}
                  onFetchDocuments={() => fetchFromSite(selectedPortal)}
                  onFilterChange={setSearchTerm}
                  onOpenModal={() => setIsResultsMaximized(true)}
                  onSearchOnlineFallback={searchOnlineAfterContingency}
                  onToggleCategory={toggleCategory}
                  portals={PORTALS}
                  selectedPortal={selectedPortal}
                  onPortalChange={handlePortalChange}
                  searchTerm={searchTerm}
                  showOnlineFallbackPrompt={shouldSuggestOnlineSearch}
                />
              ) : (
                comparatorPanel
              )}
            </div>
          </div>
        </div>

      </div>

      <div className="fixed bottom-3 right-4 sm:bottom-5 sm:right-5 md:bottom-7 md:right-7 z-40 flex flex-col items-center gap-3">
        <button
          onClick={() => setShowSupportModal(true)}
          className="h-12 w-12 sm:h-14 sm:w-14 rounded-2xl bg-gradient-to-r from-rose-500 via-pink-500 to-orange-400 text-white shadow-xl hover:shadow-2xl hover:shadow-rose-500/45 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center border border-white/20"
          title="Apoiar o projeto"
        >
          <Heart size={18} className="fill-current shrink-0" />
        </button>
      </div>

      {showEmailModal ? (
        <EmailAlertsPanel
          apiToken={apiToken}
          buildRequestHeaders={buildRequestHeaders}
          getApiUrl={getApiUrl}
          isAdminAuthenticated={isAdminAuthenticated}
          onClose={() => setShowEmailModal(false)}
          portals={PORTALS}
        />
      ) : null}

      {showWebhookModal ? (
        <WebhookAlertsModal
          apiToken={apiToken}
          buildRequestHeaders={buildRequestHeaders}
          getApiUrl={getApiUrl}
          isAdminAuthenticated={isAdminAuthenticated}
          onClose={() => setShowWebhookModal(false)}
          portals={PORTALS}
        />
      ) : null}

      {showSupportModal ? (
        <SupportModal
          config={SUPPORT_CONFIG}
          onClose={() => setShowSupportModal(false)}
        />
      ) : null}

      {showServiceStatusModal ? (
        <ServiceStatusModal
          isLoading={isLoadingServiceStatusItems}
          isLoadingWebMania={isLoadingWebManiaStatus}
          isLoadingTecnoSpeed={isLoadingTecnoSpeedStatus}
          items={serviceStatusItems}
          webManiaItems={webManiaStatusItems}
          webManiaError={webManiaError}
          tecnoSpeedItems={tecnoSpeedStatusItems}
          tecnoSpeedError={tecnoSpeedError}
          onClose={() => setShowServiceStatusModal(false)}
        />
      ) : null}

      {isDocumentLinkPage ? (
        <DocumentLinkModal
          search={documentLinkLocation.search}
          onClose={closeDocumentLinkModal}
        />
      ) : null}
    </div>
  );
}
